import ReactDOM from "react-dom/client";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import flagsmith from "flagsmith";
import { FlagsmithProvider } from "flagsmith/react";
import * as Sentry from "@sentry/react";
import "react-toastify/dist/ReactToastify.css";
import { router } from "@/navigation/router";
import { getCurrentEnvironment } from "@/utils/appManager";
import { getAppConfig } from "@/utils/appManager";
import "@/lang/setup";
import "./index.css";
import StyledToastContainer from "@/components/StyledToastContainer";
import datashipsConsole from "@/utils/datashipsConsole";
import handleToasts from "@/utils/handleToasts";
import logError from "@/utils/logError";
import handleUnauthorized from "@/utils/handleUnauthorized";
import { StrictMode } from "react";
import TagManager from "react-gtm-module";
import React from "react";

const environment = getCurrentEnvironment();
const { flagsmithId, flagsmithHost, gtmId } = getAppConfig();

const tagManagerArgs = {
  gtmId,
};

TagManager.initialize(tagManagerArgs);

if (!import.meta.env.DEV) datashipsConsole();

if (!import.meta.env.DEV)
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    environment: environment,
    ignoreErrors: [
      "Network Error",
      "Request failed with status code 401",
      "Unexpected token '<'",
    ],
  });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 3,
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      handleToasts({ type: "error", data: error });
      logError(error, query.options.queryFn?.toString());
      handleUnauthorized(error, query);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _, __, mutation) => {
      handleToasts({ type: "error", data: error });
      logError(error, mutation.options.mutationFn?.name);
    },
  }),
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <FlagsmithProvider
      flagsmith={flagsmith}
      options={{
        environmentID: flagsmithId,
        api: flagsmithHost,
        cacheFlags: true,
      }}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <ReactQueryDevtools initialIsOpen={false} />
        <StyledToastContainer />
      </QueryClientProvider>
    </FlagsmithProvider>
  </StrictMode>,
);
